import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { useAppMode } from "@/hooks/useAppMode";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  gender: string;
  smsNotificationsEnabled: boolean;
  pushNotificationsEnabled: boolean;
}

const AppAccountSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { provider } = useAppMode();
  const brandColor = provider?.brand_primary_color || undefined;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormData>();

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setStatusMsg(null);
    try {
      const { error } = await supabase.functions.invoke("delete-account");
      if (error) throw error;
      await supabase.auth.signOut();
      navigate("/app/auth");
    } catch (err) {
      setStatusMsg({
        type: "error",
        text: `Failed to delete account: ${(err as Error).message}`,
      });
      setDeleting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();
      reset({
        firstName: data?.first_name || user.user_metadata?.first_name || "",
        lastName: data?.last_name || user.user_metadata?.last_name || "",
        email: user.email || "",
        phoneNumber: data?.phone_number || "",
        dateOfBirth: data?.date_of_birth || "",
        gender: data?.gender || "",
        smsNotificationsEnabled: data?.sms_notifications_enabled ?? true,
        pushNotificationsEnabled:
          (data as any)?.push_notifications_enabled ?? true,
      });
      setLoading(false);
    };
    load();
  }, [user, reset]);

  const onSubmit = async (form: FormData) => {
    if (!user) return;
    setStatusMsg(null);
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .upsert({
          id: user.id,
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          phone_number: form.phoneNumber || null,
          date_of_birth: form.dateOfBirth || null,
          gender: form.gender || null,
          sms_notifications_enabled: form.smsNotificationsEnabled,
          push_notifications_enabled: form.pushNotificationsEnabled,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;

      // Reflect push preference on stored device tokens
      await supabase
        .from("push_tokens")
        .update({ is_active: form.pushNotificationsEnabled })
        .eq("user_id", user.id);

      // Sync user metadata so headers reflect the new name
      await supabase.auth.updateUser({
        data: { first_name: form.firstName, last_name: form.lastName },
      });

      setStatusMsg({ type: "success", text: "Your account has been updated." });
    } catch (err) {
      setStatusMsg({ type: "error", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileAppLayout title="Account">
      <div className="px-4 py-4 space-y-4">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2"
          onClick={() => navigate("/app/profile")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        <h1 className="text-2xl font-bold tracking-tight">Account settings</h1>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" disabled {...register("firstName")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" disabled {...register("lastName")} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" disabled {...register("email")} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="phoneNumber">Phone</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    inputMode="tel"
                    {...register("phoneNumber")}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="dateOfBirth">Date of birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    {...register("dateOfBirth")}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Gender</Label>
                  <Select
                    value={watch("gender") || ""}
                    onValueChange={(v) => setValue("gender", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">Push notifications</p>
                  <p className="text-xs text-muted-foreground">
                    Booking updates and session reminders on your device.
                  </p>
                </div>
                <Switch
                  checked={watch("pushNotificationsEnabled")}
                  onCheckedChange={(v) =>
                    setValue("pushNotificationsEnabled", v)
                  }
                />
              </CardContent>
            </Card>

            {statusMsg && (
              <div
                className={
                  statusMsg.type === "success"
                    ? "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                    : "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                }
              >
                {statusMsg.text}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12"
              disabled={saving}
              style={brandColor ? { backgroundColor: brandColor } : undefined}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </form>
        )}

        {!loading && (
          <div className="pt-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
              Danger zone
            </h2>
            <Card className="border-destructive/30">
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="font-medium text-sm">Delete account</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="w-full" disabled={deleting}>
                      {deleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete my account
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete your account, bookings history, memberships, and all personal data. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Yes, delete my account
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </MobileAppLayout>
  );
};

export default AppAccountSettings;
